/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FunctionComponent, useCallback, ChangeEvent, useRef, useEffect } from "react";
import { FieldLabel } from "./Fields/FieldLabel";

export enum SEARCHBOX_SIZE {
	SMALL = "small",
	MEDIUM = "medium",
	LARGE = "large",
	XLARGE = "xlarge"
}

export enum SEARCHBOX_STYLE {
	FIELD = "field",
	FIELD_NAKED_ROUND = "field-naked-round",
	TRANSPARENT = "transparent",
	TRANSPARENT_NAKED = "transparent-naked",
	TRANSPARENT_ROUND = "transparent-round",
	TRANSPARENT_NAKED_ROUND = "transparent-naked-round"
}

/**
 * Component props
 */
export interface ISearchBoxProps {
	value: string;
	className?: string;
	placeholder?: string;
	onChange: (value: string) => void;
	size?: SEARCHBOX_SIZE;
	style?: SEARCHBOX_STYLE;
	shouldFocus?: boolean;
	label?: string;
}

/**
 * Search box component
 */
export const SearchBox: FunctionComponent<ISearchBoxProps> = ({
	value,
	className,
	placeholder,
	onChange,
	size,
	style,
	shouldFocus,
	label
}) => {
	const inputRef = useRef<HTMLInputElement>();

	const handleChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			const newValue = ev.currentTarget.value;
			onChange(newValue);
		},
		[ onChange ]
	);

	useEffect(() => {
		if (shouldFocus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [ shouldFocus ]);

	const baseClass = BEM("edt-searchbox");
	baseClass.mod("size-" + (size || SEARCHBOX_SIZE.MEDIUM));
	baseClass.mod("style-" + (style || SEARCHBOX_STYLE.FIELD));

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			{label && <FieldLabel>{label}</FieldLabel>}
			<input
				ref={inputRef}
				className={baseClass.toClassName()}
				type="text"
				value={value || ""}
				placeholder={placeholder}
				onChange={handleChange}
			/>
		</>
	);
};
